import React from "react";
import { Redirect, Route } from "react-router-dom";
import { getCurrentUser } from "../services/JwtService";
import routes from "../consts/routes.json";
import PropTypes from "prop-types";
import { checkForCondition, LOGGED_IN_CONDITION, NOT_LOGGED_IN_CONDITION } from "../utils/SecurityUtils";
import {
    BEAUFTRAGTER,
    BETREIBER,
    FACHADMIN,
    LESER_BEHOERDE,
    LESER_ORGANISATION,
    SYSTEMADMIN,
    ZUST_SBA,
    ZUST_SBB,
    ZUST_SBD,
    ZUST_SBS,
} from "../consts/funktionaleRollen";

/**
 * Komponente, welche eine Route nur zur Verfügung stellt, falls der Anwender die angegebenen Conditions erfüllt.
 * Diese können entweder NOT_LOGGED_IN (NOT_LOGGED_IN_CONDITION aus SecurityUtils), LOGGED_IN (LOGGED_IN_CONDITION aus SecurityUtils)
 * oder ein Array aus funktionalen Rollen sein.
 * Ist der Anwender nicht eingeloggt, muss dies aber sein, wird er zum Login geführt und nach dem Login an die Stelle geführt, welche er
 * mit der URL eigentlich aufrufen wollte.
 * Ist der Anwender eingeloggt, dürfte dies für die Route aber nicht sein, wird er zur Startseite weitergeleitet.
 * Ist der Anwender angemeldet, hat allerdings nicht die benötigte Rolle, wird er zu einer "Zugriff verweigert" Maske weitergeleitet
 * @param path Die Url, welche zur Komponente führt
 * @param Component die anzuzeigende Komponente (entweder Component oder render muss angegeben werden)
 * @param render(props) nur relevant, falls Component nicht angegeben wird. Rendert eine Komponente und es können weitere Attribute
 * übergeben werden
 * @param condition Array der erlaubten funktionalen Rollen oder die LOGGED_IN_CONDITION bzw. NOT_LOGGED_IN_CONDITION aus SecurityUtils
 * @param rest
 * @returns {JSX.Element}
 * @constructor
 */
function EARoute({ path, component: Component, render, condition, ...rest }) {
    const user = getCurrentUser();
    function buildConditionalComponent(props) {
        if (!checkForCondition(condition, NOT_LOGGED_IN_CONDITION) && !user) {
            return (
                <Redirect
                    to={{
                        pathname: routes.LOGIN,
                        state: { from: props.location },
                    }}
                />
            );
        }
        if (
            (checkForCondition(condition, NOT_LOGGED_IN_CONDITION) && !user) ||
            (user && (checkForCondition(condition, LOGGED_IN_CONDITION) || user.funktionaleRollen.find((role) => condition.includes(role))))
        ) {
            return Component ? <Component {...props} /> : render(props);
        }
        if (checkForCondition(condition, NOT_LOGGED_IN_CONDITION) && user) {
            return <Redirect to={routes.STARTSEITE} />;
        }
        return <Redirect to={routes.UNAUTORISIERT} />;
    }

    return <Route {...rest} path={path} render={(props) => buildConditionalComponent(props)} />;
}

EARoute.propTypes = {
    path: PropTypes.string.isRequired,
    condition: PropTypes.arrayOf(
        PropTypes.oneOf([
            "NOT_LOGGED_IN",
            "LOGGED_IN",
            BETREIBER,
            BEAUFTRAGTER,
            LESER_ORGANISATION,
            ZUST_SBD,
            ZUST_SBB,
            ZUST_SBS,
            ZUST_SBA,
            LESER_BEHOERDE,
            FACHADMIN,
            SYSTEMADMIN,
        ])
    ).isRequired,
    component: PropTypes.elementType,
    render: PropTypes.func,
};

export default EARoute;
